import { JOURS_TRAVAILLES_PAR_MOIS, enJournees } from "@riseafrica/scoring";
import { fmt } from "../lib/format";
import { shareWhatsApp } from "../lib/share";
import { useApp } from "../state/AppState";
import type { Volet2 as Volet2Result } from "../state/AppState";
import { Offers } from "../components/Offers";
import { Fil } from "../components/Fil";
import { RappelAgenda } from "../components/RappelAgenda";

/** Volet 02 — restitution du temps récupérable et de sa valeur annuelle. */
export function Result2({ t2 }: { t2: Volet2Result }) {
  const { goTo, registerShare, aSonCompte } = useApp();
  const lignes = t2.rows.filter((r) => r.heures > 0).sort((a, b) => b.recuperables - a.recuperables);

  /*
    La journée de travail, unité que tout le monde manie.

    « 74 heures par mois » demande un calcul mental que personne ne fait ;
    « neuf journées » se voit tout de suite. Le seuil de 8 h évite d'annoncer
    « 0 journée » quand le volume est faible — auquel cas on parle en heures.
  */
  const journees = enJournees(t2.hours);

  return (
    <section>
      <div className="shell narrow app">
        <Fil etape={2} fait />
        <div className="verdict">
          <div className="kicker">Temps récupérable</div>
          <div className="bignum">
            {fmt(t2.hours)}
            <small>
              heures par mois
              {journees > 0 ? ` — soit ${journees} journées sur les ${JOURS_TRAVAILLES_PAR_MOIS} d'un mois` : ""}
            </small>
          </div>
          <div className="bignum money">
            {fmt(t2.money)} F<small>ce que ce temps coûte sur une année</small>
          </div>
          {/*
            À qui appartient cet argent — la question que le montant seul ne
            réglait pas.

            « 1 129 000 F par an, à votre niveau de rémunération » était exact
            et illisible : un salarié ne peut pas savoir s'il s'agit de ce qu'il
            gagnerait, de ce qu'il coûte, ou de ce qu'un autre économise. Le
            chiffre le plus voyant de l'écran restait donc sans propriétaire, et
            l'enjeu — qui est précisément là — disparaissait avec lui.

            On nomme les deux lectures. Aucune n'est une prédiction : la
            première décrit ce que le nombre est, la seconde ce que n'importe
            qui peut calculer avec les mêmes heures. Le produit ne prédit pas de
            licenciement, il dit ce qui est déjà mesurable.
          */}
          <div className="enjeu">
            <p>
              <b>Ce montant n'est pas un gain qui vous attend.</b> C'est le prix
              d'une année du temps que vous passez sur des tâches qu'un outil
              fait déjà, au niveau de rémunération que vous venez d'indiquer.
            </p>
            {/* Le statut est déclaré à l'accueil : un seul paragraphe, le sien. */}
            {aSonCompte ? (
              <p>
                <b>À votre compte</b>, c'est votre argent, directement :{" "}
                {journees > 0 ? `${journees} journées par mois` : "ces heures"} que vous pourriez
                consacrer à ce qui se facture.
              </p>
            ) : (
              <p>
                <b>Salarié</b>, cet argent n'est pas le vôtre : c'est ce que votre employeur paie
                pour ce temps. Rien ne l'empêche de faire le même calcul. Le connaître avant lui,
                c'est pouvoir dire ce que vous faites de{" "}
                {journees > 0 ? `ces ${journees} journées` : "ces heures"}.
              </p>
            )}
            <p className="fine">
              Ces heures ne s'évaporent pas — elles se déplacent vers la part de votre métier
              qu'aucun outil ne fait. C'est elle qui décide désormais de ce que vaut votre travail.
            </p>
          </div>
        </div>

        <div className="block">
          <h2>D'où vient ce temps</h2>
          <p className="sub">
            Part automatisable de chaque tâche, selon les outils disponibles aujourd'hui.
          </p>
          <div>
            {lignes.map((ligne) => (
              <div className="row" key={ligne.name}>
                <div className="task">
                  {ligne.name}
                  <span className="src">{fmt(ligne.heures)} h déclarées par semaine</span>
                </div>
                <i className={`chip ${ligne.auto >= 0.7 ? "c-high" : "c-mid"}`}>
                  {Math.round(ligne.recuperables)} h/mois
                </i>
              </div>
            ))}
          </div>
        </div>

        {/*
          Le volet 3 passe avant les offres, et non l'inverse.
          L'écran vendait le rapport, puis la fenêtre de paiement répondait
          « vous n'avez pas encore passé le volet 3, il change le rapport » :
          le parcours se contredisait à l'endroit précis où il encaisse. Le
          bouton reste une invitation, pas un péage — les offres sont juste en
          dessous pour qui veut s'arrêter là.
        */}
        <button
          type="button"
          className="btn"
          onClick={() => goTo("s-t3")}
        >
          Dernier volet : votre niveau de maîtrise →
        </button>
        <p className="hint">
          Deux minutes. C'est lui qui décide si vos dix actions sont écrites pour quelqu'un qui
          débute ou pour quelqu'un qui s'en sert déjà tous les jours — et il délivre votre attestation de niveau.
        </p>

        <div className="block">
          <h2>Ou obtenez votre rapport maintenant</h2>
          <p className="sub">
            Les trois volets sont libres : le rapport s'achète à tout moment. Sans le volet 3, il
            reste juste — simplement écrit sans savoir où vous en êtes.
          </p>
          <Offers money={t2.money} />
        </div>

        <div className="block">
          <div className="sharecard">
            <div className="small">Ce que ces tâches me coûtent</div>
            <div className="big">
              {fmt(t2.hours)} h/mois · {fmt(t2.money)} F/an
            </div>
            <div className="small">STRATERA · coût du temps</div>
          </div>
          <button
            type="button"
            className="btn btn-wa"
            onClick={() => {
              registerShare();
              shareWhatsApp(
                `Je perds ${fmt(t2.hours)} heures par mois sur des tâches automatisables, soit ${fmt(t2.money)} F par an. Calcule les tiennes :`,
              );
            }}
          >
            Envoyer sur WhatsApp
          </button>
        </div>

        <RappelAgenda
          proeminent
          titre="Re-mesurer ce que mon temps me coûte"
          mesure={`Mesure précédente : ${fmt(t2.hours)} h par mois récupérables, ${fmt(t2.money)} F par an.`}
          pourquoi="Ce montant suit la façon dont vous occupez vos semaines. Chaque tâche confiée à une IA le déplace, et c'est l'écart qui dit si quelque chose a bougé."
          accroche="Ce calcul vaut trois mois"
          ecran="r2"
        />

        <button type="button" className="back" onClick={() => goTo("home")}>
          ← Revenir au diagnostic
        </button>
      </div>
    </section>
  );
}
